/**
 * Analytics Utility Module
 * Tracks to both Firebase Analytics (for GA4/BigQuery) and Firestore (for participant-specific metrics)
 */

import firebase from "firebase/app";
import "firebase/analytics";
import "firebase/firestore";

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Retry transient Firestore failures (same policy as static-analytics.js).
 */
function isRetryableFirestoreError(error) {
  if (!error || !error.code) {
    return false;
  }
  const c = String(error.code);
  return (
    c.indexOf("unavailable") !== -1 ||
    c.indexOf("resource-exhausted") !== -1 ||
    c.indexOf("deadline-exceeded") !== -1 ||
    c.indexOf("aborted") !== -1 ||
    c.indexOf("internal") !== -1
  );
}

async function runWithRetry(runFn, maxAttempts = 4) {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await runFn();
    } catch (error) {
      if (!isRetryableFirestoreError(error) || attempt === maxAttempts) {
        throw error;
      }
      const delayMs = Math.min(3200, 200 * Math.pow(2, attempt - 1));
      await sleep(delayMs);
    }
  }
}

/**
 * Track user signup/registration event
 * @param {string} userId - The authenticated user's UID
 * @param {string} method - Registration method (e.g., 'password', 'google')
 */
export async function trackSignup(userId, method = 'password') {
  try {
    console.log('[Analytics] trackSignup called for user:', userId);
    
    // Send to Firebase Analytics (feeds into GA4 and BigQuery)
    const analytics = firebase.analytics();
    analytics.logEvent('sign_up', {
      method: method,
      user_id: userId,
      timestamp: new Date().toISOString()
    });

    // Set user ID for all subsequent events
    analytics.setUserId(userId);

    // Track in Firestore for participant-specific metrics
    const db = firebase.firestore();
    const signupDoc = {
      userId: userId,
      timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      method: method,
      type: 'signup'
    };

    // Add to user's signup history collection
    await db.collection('users')
      .doc(userId)
      .collection('signupHistory')
      .add(signupDoc);

    // Initialize user document with signup info
    await db.collection('users')
      .doc(userId)
      .set({
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        signupDate: new Date().toISOString().split('T')[0],
        signupMethod: method
      }, { merge: true });

    console.log('[Analytics] Signup event tracked successfully');
    return true;
  } catch (error) {
    console.error('[Analytics] Error tracking signup:', error);
    return false;
  }
}

/**
 * Track user login event
 * Requirement IDs: 1, 2, 3, 4
 * @param {string} userId - The authenticated user's UID
 * @param {string} method - Authentication method (e.g., 'google')
 */
export async function trackLogin(userId, method = 'google') {
  try {
    console.log('[Analytics] trackLogin called for user:', userId);
    
    // Send to Firebase Analytics (feeds into GA4 and BigQuery)
    const analytics = firebase.analytics();
    analytics.logEvent('login', {
      method: method,
      user_id: userId,
      timestamp: new Date().toISOString()
    });

    // Set user ID for all subsequent events
    analytics.setUserId(userId);

    // Track in Firestore for participant-specific metrics
    const db = firebase.firestore();
    const loginDoc = {
      userId: userId,
      timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      method: method,
      type: 'login'
    };

    // Add to user's login history collection
    await db.collection('users')
      .doc(userId)
      .collection('loginHistory')
      .add(loginDoc);

    // Check if user already has an enrollmentDate (don't overwrite on subsequent logins)
    const userDoc = await db.collection('users').doc(userId).get();
    const hasEnrollmentDate = userDoc.exists && userDoc.data().enrollmentDate;

    // Update user's last login timestamp and increment login count
    const userUpdate = {
      lastLogin: firebase.firestore.FieldValue.serverTimestamp(),
      lastLoginDate: new Date().toISOString().split('T')[0],
      totalLogins: firebase.firestore.FieldValue.increment(1),
      userId: userId
    };

    // Set enrollmentDate only on first tracked login
    if (!hasEnrollmentDate) {
      userUpdate.enrollmentDate = firebase.firestore.FieldValue.serverTimestamp();
      console.log('[Analytics] Setting enrollmentDate for new user:', userId);
    }

    await db.collection('users')
      .doc(userId)
      .set(userUpdate, { merge: true });

    console.log('Login tracked successfully for user:', userId);
  } catch (error) {
    console.error('[Analytics] Error tracking login:', error);
    console.error('[Analytics] Error details:', error.message, error.code);
  }
}

/**
 * Track page view
 * Requirement ID: 5
 * @param {string} pageName - Name/title of the page
 * @param {string} pagePath - URL path of the page
 * @param {string} userId - The authenticated user's UID
 */
export function trackPageView(pageName, pagePath, userId = null) {
  try {
    const analytics = firebase.analytics();
    analytics.logEvent('page_view', {
      page_title: pageName,
      page_path: pagePath,
      user_id: userId
    });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
}

/**
 * Check if current visit is during the tailored messaging period (weeks 5-12)
 * @param {Date|null} enrollmentDate - User's enrollment date
 * @returns {boolean} True if in weeks 5-12, false otherwise
 */
function isInTailoredMessagingPeriod(enrollmentDate) {
  if (!enrollmentDate) return false;

  const weekNumber = getStudyWeek(enrollmentDate);
  return weekNumber >= 5 && weekNumber <= 12;
}

/**
 * Atomic read user + app module doc, then set/update appModuleViews (same fields as before).
 */
function persistAppModuleAccessToFirestore(
  db,
  userId,
  moduleType,
  moduleName,
  timestamp
) {
  const userRef = db.collection("users").doc(userId);
  const moduleDocId = `${moduleType}_${moduleName}`;
  const moduleRef = userRef.collection("appModuleViews").doc(moduleDocId);

  return db.runTransaction(function (transaction) {
    return Promise.all([transaction.get(userRef), transaction.get(moduleRef)]).then(
      function (snapshots) {
        const userDoc = snapshots[0];
        const moduleDoc = snapshots[1];

        let enrollmentDate = null;
        if (userDoc.exists && userDoc.data().enrollmentDate) {
          enrollmentDate = userDoc.data().enrollmentDate.toDate();
        }

        const isTailoredMessaging = isInTailoredMessagingPeriod(enrollmentDate);
        const isStandardGuidance = isInStandardGuidancePeriod(enrollmentDate);

        if (moduleDoc.exists) {
          const updates = {
            lastVisit: firebase.firestore.FieldValue.serverTimestamp(),
            allViews: firebase.firestore.FieldValue.arrayUnion(timestamp)
          };

          if (isTailoredMessaging) {
            updates.tailoredMessagingViews =
              firebase.firestore.FieldValue.arrayUnion(timestamp);
          }

          if (isStandardGuidance) {
            updates.standardGuidanceViews =
              firebase.firestore.FieldValue.arrayUnion(timestamp);
          }

          transaction.update(moduleRef, updates);
        } else {
          transaction.set(moduleRef, {
            moduleType: moduleType,
            moduleName: moduleName,
            firstVisit: firebase.firestore.FieldValue.serverTimestamp(),
            lastVisit: firebase.firestore.FieldValue.serverTimestamp(),
            tailoredMessagingViews: isTailoredMessaging ? [timestamp] : [],
            standardGuidanceViews: isStandardGuidance ? [timestamp] : [],
            allViews: [timestamp]
          });
        }
      }
    );
  });
}

/**
 * Track module access (tailored messaging for weeks 5-12, standard guidance for weeks 1-4)
 * Requirement IDs: 1, 3
 * @param {string} moduleType - 'symptom' or 'behavior'
 * @param {string} moduleName - Name of the module (e.g., 'sleep', 'pain')
 * @param {string} userId - The authenticated user's UID
 */
export async function trackModuleAccess(moduleType, moduleName, userId) {
  try {
    const timestamp = new Date().toISOString();
    const analytics = firebase.analytics();
    analytics.logEvent("module_access", {
      module_type: moduleType,
      module_name: moduleName,
      user_id: userId,
      timestamp: timestamp
    });

    const db = firebase.firestore();
    await runWithRetry(function () {
      return persistAppModuleAccessToFirestore(
        db,
        userId,
        moduleType,
        moduleName,
        timestamp
      );
    }, 4);
  } catch (error) {
    console.error("Error tracking module access:", error);
  }
}

/**
 * Track session start
 * Requirement ID: 6
 * @param {string} userId - The authenticated user's UID
 */
export async function trackSessionStart(userId) {
  try {
    const analytics = firebase.analytics();
    analytics.logEvent('session_start', {
      user_id: userId
    });

    // Store session start in Firestore under user's sessions subcollection
    const db = firebase.firestore();
    const sessionId = Date.now().toString(); // Just timestamp, no userId prefix

    await db.collection('users')
      .doc(userId)
      .collection('sessions')
      .doc(sessionId)
      .set({
        userId: userId,
        startTime: firebase.firestore.FieldValue.serverTimestamp(),
        date: new Date().toISOString().split('T')[0],
        type: 'session'
      });

    // Store session ID in sessionStorage for later session end tracking
    sessionStorage.setItem('currentSessionId', sessionId);
    sessionStorage.setItem('sessionStartTime', Date.now().toString());

  } catch (error) {
    console.error('Error tracking session start:', error);
  }
}

/**
 * Track session end
 * Requirement ID: 6
 * @param {string} userId - The authenticated user's UID
 */
export async function trackSessionEnd(userId) {
  try {
    const sessionId = sessionStorage.getItem('currentSessionId');
    const startTime = parseInt(sessionStorage.getItem('sessionStartTime') || '0');
    const duration = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0; // in seconds

    const analytics = firebase.analytics();
    analytics.logEvent('session_end', {
      user_id: userId,
      session_duration: duration
    });

    // Update session end in Firestore under user's sessions subcollection
    if (sessionId) {
      const db = firebase.firestore();
      await db.collection('users')
        .doc(userId)
        .collection('sessions')
        .doc(sessionId)
        .update({
          endTime: firebase.firestore.FieldValue.serverTimestamp(),
          duration: duration
        });
    }

    // Clear session data
    sessionStorage.removeItem('currentSessionId');
    sessionStorage.removeItem('sessionStartTime');

  } catch (error) {
    console.error('Error tracking session end:', error);
  }
}

/**
 * Get user enrollment date from Firestore
 * Helper function to calculate week number for study period tracking
 * @param {string} userId - The authenticated user's UID
 * @returns {Promise<Date|null>} Enrollment date or null if not found
 */
export async function getUserEnrollmentDate(userId) {
  try {
    const db = firebase.firestore();
    const userDoc = await db.collection('users').doc(userId).get();

    if (userDoc.exists) {
      const data = userDoc.data();
      if (data.enrollmentDate) {
        return data.enrollmentDate.toDate();
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting enrollment date:', error);
    return null;
  }
}

/**
 * Calculate study week number based on enrollment date
 * @param {Date} enrollmentDate - User's enrollment date
 * @returns {number} Current week number (1-12+)
 */
export function getStudyWeek(enrollmentDate) {
  if (!enrollmentDate) return 0;

  const now = new Date();
  const diffTime = now - enrollmentDate;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const weekNumber = Math.ceil(diffDays / 7);

  return weekNumber;
}

/**
 * Check if current visit is during the standard guidance period (weeks 1-4)
 * @param {Date|null} enrollmentDate - User's enrollment date
 * @returns {boolean} True if in weeks 1-4, false otherwise
 */
export function isInStandardGuidancePeriod(enrollmentDate) {
  if (!enrollmentDate) return false;

  const weekNumber = getStudyWeek(enrollmentDate);
  return weekNumber >= 1 && weekNumber <= 4;
}
